import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import {
  TextInput,
  PasswordInput,
  Button,
  Title,
  Text,
  Divider,
  Stack,
  Anchor
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { IconEye, IconEyeOff } from '@tabler/icons-react'
import { AuthLayout } from '../components/layout/AuthLayout'
import { notifications } from '@mantine/notifications'

function SignIn() {
  const { signIn, loading: authLoading, user, session } = useAuth()
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  
  // Redirect if already authenticated
  useEffect(() => {
    if (session && user) {
      const redirectPath = user.role === 'admin' ? '/admin/dashboard' : '/dashboard'
      navigate(redirectPath)
    }
  }, [session, user, navigate])

  const form = useForm({
    initialValues: {
      email: '',
      password: ''
    },
    validate: {
      email: (value) => {
        if (!value) return 'Email is required'
        if (!/^\S+@\S+$/.test(value)) return 'Invalid email address'
        return null
      },
      password: (value) => {
        if (!value) return 'Password is required'
        if (value.length < 6) return 'Password must be at least 6 characters'
        return null
      }
    }
  })

  const isAdminEmail = form.values.email.toLowerCase().includes('admin')

  const handleSubmit = async (values) => {
    setLoading(true)
    
    try {
      const result = await signIn(values.email, values.password)
      
      if (!result.success) {
        notifications.show({
          title: 'Sign In Failed',
          message: result.error || 'Invalid credentials',
          color: 'red'
        })
        setLoading(false)
      } else {
        // Success notification - navigation handled by AuthContext
        notifications.show({
          title: 'Welcome back!',
          message: 'Signing you in...',
          color: 'green'
        })
      }
    } catch (error) {
      if (import.meta.env.DEV) console.error('Sign in error:', error)
      notifications.show({
        title: 'Error',
        message: 'An unexpected error occurred',
        color: 'red'
      })
      setLoading(false)
    }
  }

  return (
    <AuthLayout>
            <Title order={1} size="2.5rem" mb="sm">
              Sign In
            </Title>
            
            <Text c="dimmed" mb="xl">
              New user? <Anchor component={Link} to="/signup">Create an account</Anchor>
            </Text>

            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Stack>
                <TextInput
                  label="Email Address"
                  placeholder="your@email.com"
                  size="md"
                  {...form.getInputProps('email')}
                />

                <PasswordInput
                  label="Password"
                  placeholder="Your password"
                  size="md"
                  visibilityToggleIcon={({ reveal }) =>
                    reveal ? <IconEyeOff size={20} /> : <IconEye size={20} />
                  }
                  {...form.getInputProps('password')}
                />

                <Anchor component={Link} to="/forgot-password" size="sm" c="dimmed">
                  Forgot password?
                </Anchor>

                <Button
                  type="submit"
                  size="lg"
                  loading={loading}
                  disabled={loading}
                  fullWidth
                  mt="md"
                  color={isAdminEmail ? 'grape' : 'blue'}
                >
                  Sign In{isAdminEmail ? ' as Admin' : ''}
                </Button>
              </Stack>
            </form>
    </AuthLayout>
  )
}

export default SignIn